import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { Duration } from "luxon";

import { getUserPresentableMessage } from "@core/foundation/error";
import type { LoggerContract } from "@core/foundation/logger";
import { hasValue } from "@core/foundation/types";
import { HttpStatusError, StatusCodes } from "@core/ports/http";

import { toast } from "@react/ui-primitives/extension";

export function createQueryClient(logger: LoggerContract): QueryClient {
  const RETRY_COUNT = 3;

  const RETRY_DELAY = Duration.fromObject({ seconds: 1 });

  return new QueryClient({
    defaultOptions: {
      mutations: { retry: false },
      queries: {
        retry: (failureCount, error) =>
          failureCount < RETRY_COUNT && !isSettledAnswer(error),
        retryDelay: attemptIndex => RETRY_DELAY.toMillis() * 2 ** attemptIndex,
      },
    },
    mutationCache: new MutationCache({
      onError: error => {
        logger.error("Mutation error", error);
        toastMutationError(error);
      },
    }),
    queryCache: new QueryCache({
      onError: (error, { queryKey }) => {
        logger.error("Query error", queryKey, error);
      },
    }),
  });
}

function isSettledAnswer(error: unknown): boolean {
  const statusCode = HttpStatusError.getStatusCode(error);

  return (
    hasValue(statusCode) &&
    statusCode >= StatusCodes.BAD_REQUEST &&
    statusCode < StatusCodes.INTERNAL_SERVER_ERROR
  );
}

function toastMutationError(error: unknown): void {
  const GENERIC_MESSAGE = "Something went wrong. Please try again.";

  if (HttpStatusError.getStatusCode(error) === StatusCodes.UNAUTHORIZED) {
    return;
  }

  const message = getUserPresentableMessage(error, GENERIC_MESSAGE);
  toast.error(message, { id: message });
}
